import React, { forwardRef, memo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass/styled-components';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { LoadMore } from 'components/atoms/LoadMore';
import { TableRow } from './components/tableRow';
import { TableHeader } from './components/tableHeader';
import CustomCheckboxRenderer from './components/customCheckboxRenderer';
import { useTable } from './hooks/useTable';

import { TableEmpty } from './components/tableEmpty';
import TableActions from './components/tableActions';
import { GlobalTableTheme } from './hooks/useTableTheme';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);
LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_KEY);

const DataTable = forwardRef(
  (
    {
      itemName,
      items,
      hasMore,
      loadMore,
      isLoadingMore,
      columnDefinition,
      canSort,
      rowActions,
      initialSortBy,
      totalItems,
      emptyView,
      isInitialized,
      sx,
      onSort,
      onSelect,
      canSelect,
      selectedAll,
      persistColumnKey,
      showTableActions,
      FiltersComponent,
      leftActions,
      rightActions,
      topBanner,
      tableActions,
      onColumnAction,
      components,
      cellStyle,
      gridOptions,
      tableActionsSx,
      useHorizontalActions,
      sortColumns,
      enableLeftTableBorder,
      defaultColDef,
      companyFilters,
      showActions,
      useExternalStoredColumns,
      storedColumns,
      setStoredColumns,
      filterSx,
      hasBulkActions,
    },
    ref,
  ) => {
    const {
      tableRef,
      selectedColumns,
      setSelectedColumns,
      selectedColumn,
      setSelectedColumn,
      showEmptyView,
      handleSort,
      columnDefs,
      theme,
      onColumnMoved,
      sortedColumns,
      onColumnResized,
      hideColumn,
    } = useTable({
      items,
      columnDefinition,
      canSelect,
      onSelect,
      selectedAll,
      isLoadingMore,
      initialSortBy,
      onSort,
      persistColumnKey,
      onColumnAction,
      cellStyle,
      useHorizontalActions,
      canSort,
      sortColumns,
      isInitialized,
      useExternalStoredColumns,
      storedColumns,
      setStoredColumns,
    });
    const timerRef = useRef(null);

    useEffect(
      () => () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      },
      [],
    );

    return (
      <Box>
        <GlobalTableTheme enableLeftTableBorder={enableLeftTableBorder} canSelect={canSelect} />

        {showActions && (
          <TableActions
            tableRef={tableRef}
            sortColumns={sortColumns}
            sx={tableActionsSx}
            leftActions={leftActions}
            rightActions={rightActions}
            topBanner={topBanner}
            showTableActions={showTableActions}
            itemName={itemName}
            onColumnAction={onColumnAction}
            initialSortBy={initialSortBy}
            canSort={canSort}
            selectedColumns={selectedColumns}
            selectedColumn={selectedColumn}
            setSelectedColumns={setSelectedColumns}
            setSelectedColumn={setSelectedColumn}
            tableActions={tableActions}
            handleSort={handleSort}
            sortedColumns={sortedColumns}
            totalItems={totalItems}
          />
        )}
        <Flex sx={{ zIndex: 1, position: 'relative' }}>
          {FiltersComponent && (
            <Box
              sx={{
                transform: companyFilters?.showListFilters ? 'translateX(0)' : 'translateX(-100%)',
                opacity: companyFilters?.showListFilters ? 1 : 0,
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out, max-width 0.3s ease-in-out',
                flexShrink: 0,
                overflow: 'hidden',
                width: '264px',
                maxWidth: companyFilters?.showListFilters ? '264px' : '0px',
                pointerEvents: companyFilters?.showListFilters ? 'auto' : 'none',
                ...filterSx,
              }}
            >
              <FiltersComponent {...companyFilters} />
            </Box>
          )}
          <Box
            className="ag-theme-alpine"
            sx={{
              width: '100%',
              transition: 'margin-left 0.3s ease-in-out, padding-bottom 0.3s ease-in-out',
              marginLeft: FiltersComponent && companyFilters?.showListFilters ? 0 : 0,
              // Add padding-bottom when bulk actions bar is shown or during loading
              paddingBottom: () => {
                if (hasBulkActions && isLoadingMore) return '140px';
                if (hasBulkActions) return '80px';
                if (isLoadingMore) return '80px';
                return 0;
              },
              ...sx,
            }}
          >
            <AgGridReact
              alwaysShowHorizontalScroll
              suppressHorizontalScroll={false}
              ref={tableRef}
              rowData={isInitialized ? items : []}
              theme={theme}
              columnDefs={columnDefs}
              pagination={false}
              rowHeight={60}
              headerHeight={32}
              groupDefaultExpanded={-1}
              animateRows
              cellSelection
              suppressDragLeaveHidesColumns
              suppressContextMenu
              groupDisplayType="multipleColumns"
              noRowsOverlayComponent="overlayNoRowsTemplate"
              onColumnMoved={onColumnMoved}
              onColumnResized={onColumnResized}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: false,
                headerCheckbox: false,
                enableClickSelection: false,
              }}
              noRowsOverlayComponentParams={{
                showEmptyView,
                emptyView,
                isLoadingMore,
                isInitialized,
                selectedColumns,
              }}
              defaultColDef={{
                sortable: false,
                lockPinned: true,
                ...defaultColDef,
              }}
              components={{
                customCheckboxRenderer: CustomCheckboxRenderer,
                tableHeader: TableHeader,
                tableRow: TableRow,
                overlayNoRowsTemplate: TableEmpty,
                ...components,
              }}
              autoGroupColumnDef={{
                minWidth: 300,
                headerComponent: 'tableHeader',
                pinned: 'left',
                lockPinned: 'left',
                lockPosition: 'left',
                suppressMovable: true,
                sortable: false,
                sort: false,
                cellClass: ({ value }) => {
                  if (typeof value === 'undefined' || value === 'exclude') {
                    return 'span-cell';
                  }

                  return '';
                },
                cellRendererParams: {
                  suppressCount: true,
                  suppressDoubleClickExpand: true,
                  suppressSorting: true,
                  innerRenderer: 'groupRenderer',
                },
              }}
              context={{
                rowActions,
                columns: columnDefs.reduce((acc, col) => {
                  acc[col.field] = col;
                  return acc;
                }, {}),
              }}
              getRowHeight={({ data, node }) => {
                if (node.group) {
                  return 40;
                }

                return data?.getRowHeight?.() || 48;
              }}
              onPaginationChanged={() => {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                  const verticalScrollbar = document.querySelector('.ag-body-vertical-scroll');
                  const isVisible = verticalScrollbar && verticalScrollbar.offsetWidth > 0;

                  if (!isVisible && isInitialized && hasMore && !isLoadingMore) {
                    loadMore();
                  }
                }, 1000);
              }}
              getRowId={(params) => (params.data.rowId || params.data.id).toString()}
              onBodyScrollEnd={(e) => {
                if (e.type !== 'bodyScrollEnd' || e.direction !== 'vertical') {
                  return;
                }

                // Get the scrollable viewport element
                const viewport = document.querySelector('.ag-body-viewport');
                if (!viewport) {
                  return;
                }

                // Check if we're near the bottom (within 100px threshold)
                const { scrollTop, scrollHeight, clientHeight } = viewport;
                const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
                const endReached = distanceFromBottom < 100;

                if (endReached && isInitialized && hasMore && !isLoadingMore) {
                  loadMore();
                }
              }}
              onGridReady={(params) => {
                if (ref) {
                  // eslint-disable-next-line no-param-reassign
                  ref.current = {
                    api: params.api,
                    hideColumn,
                  };
                }
              }}
              {...gridOptions}
            />
          </Box>
          {Boolean(items.length) && isLoadingMore && isInitialized && (
            <Box
              style={{
                padding: 0,
                border: 'none',
                position: 'absolute',
                bottom: hasBulkActions ? '46px' : '-16px',
                left: 0,
                right: 0,
                zIndex: 1000,
                bg: '#ffffff',
              }}
            >
              <Flex justifyContent="center" alignItems="center" width="100%" py={3}>
                <LoadMore />
              </Flex>
            </Box>
          )}
        </Flex>
      </Box>
    );
  },
);

DataTable.propTypes = {
  itemName: PropTypes.string,
  items: PropTypes.array.isRequired,
  totalItems: PropTypes.number,
  hasMore: PropTypes.bool,
  loadMore: PropTypes.func,
  columnDefinition: PropTypes.array.isRequired,
  canSort: PropTypes.bool,
  rowActions: PropTypes.shape({ height: PropTypes.number, items: PropTypes.arrayOf(PropTypes.object) }),
  initialSortBy: PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool }),
  emptyView: PropTypes.node,
  isInitialized: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  sx: PropTypes.object,
  onSort: PropTypes.func,
  onSelect: PropTypes.func,
  canSelect: PropTypes.bool,
  selectedAll: PropTypes.bool,
  onColumnAction: PropTypes.func,
  persistColumnKey: PropTypes.string,
  showTableActions: PropTypes.bool,
  FiltersComponent: PropTypes.elementType,
  leftActions: PropTypes.node,
  rightActions: PropTypes.node,
  topBanner: PropTypes.node,
  tableActions: PropTypes.node,
  gridOptions: PropTypes.object,
  components: PropTypes.object,
  cellStyle: PropTypes.object,
  tableActionsSx: PropTypes.object,
  useHorizontalActions: PropTypes.bool,
  sortColumns: PropTypes.array,
  enableLeftTableBorder: PropTypes.bool,
  defaultColDef: PropTypes.object,
  companyFilters: PropTypes.object,
  showActions: PropTypes.bool,
  useExternalStoredColumns: PropTypes.bool,
  storedColumns: PropTypes.array,
  setStoredColumns: PropTypes.func,
  filterSx: PropTypes.object,
  hasBulkActions: PropTypes.bool,
};

DataTable.defaultProps = {
  itemName: 'job',
  hasMore: false,
  totalItems: 0,
  loadMore: () => {},
  canSort: false,
  rowActions: null,
  initialSortBy: { id: 'asc', desc: false },
  emptyView: null,
  isInitialized: false,
  isLoadingMore: false,
  sx: {},
  onSort: () => {},
  onSelect: () => {},
  canSelect: false,
  selectedAll: false,
  onColumnAction: () => {},
  persistColumnKey: null,
  showTableActions: false,
  FiltersComponent: null,
  leftActions: null,
  rightActions: null,
  topBanner: null,
  tableActions: null,
  components: {},
  cellStyle: {},
  gridOptions: {},
  tableActionsSx: {},
  useHorizontalActions: true,
  sortColumns: [],
  enableLeftTableBorder: false,
  defaultColDef: {},
  companyFilters: {},
  showActions: true,
  useExternalStoredColumns: false,
  storedColumns: [],
  setStoredColumns: () => {},
  filterSx: {},
  hasBulkActions: false,
};

export default memo(DataTable);
